import React, { useCallback, useState } from 'react'
import { CanvasHeader } from '../components/canvas/CanvasHeader.jsx'
import { StatusBanner } from '../components/canvas/StatusBanner.jsx'
import { SectionDivider } from '../components/canvas/SectionDivider.jsx'
import { CardTray } from '../components/canvas/CardTray.jsx'
import { GoalsSection } from '../components/canvas/sections/GoalsSection.jsx'
import { AgendaPlaceholder } from '../components/canvas/sections/AgendaPlaceholder.jsx'
import { PrepSection } from '../components/canvas/sections/PrepSection.jsx'
import { InSessionSection } from '../components/canvas/sections/InSessionSection.jsx'
import { TasksSection } from '../components/canvas/sections/TasksSection.jsx'
import { PrivateNotesCard } from '../components/PrivateNotesCard.jsx'
import { ManagerNotesPanel } from '../components/ManagerNotesPanel.jsx'
import { Button } from '../ui'
import { SubmitPrepModal } from '../components/SubmitPrepModal.jsx'
import { usePrep } from '../hooks/usePrep.js'

// ─── deriveCanvasState ────────────────────────────────────────────────────────

/**
 * Derives the lock/unlock state of each section from session.status and viewer.
 *
 * This is a UX affordance only. The backend stays authoritative — a 409 on a
 * stale edit surfaces through the existing hook error paths.
 *
 * @param {object} session — session object with at least { status }
 * @param {object} viewer  — { userId, isManager }
 * @returns {object}        — section state flags
 */
export function deriveCanvasState(session, viewer) {
  const status = session?.status ?? 'draft'
  const isManagerView = viewer && session
    ? (viewer.userId !== session?.userId)
    : false

  const isDraft = status === 'draft'
  const isInSession = status === 'in-session'
  const isCompleted = status === 'completed'
  const isCancelled = status === 'cancelled'
  const isLocked = isCompleted || isCancelled

  return {
    // Prep: editable only in draft for the report; managers are always read-only
    prepEditable: isDraft && !isManagerView,
    prepLocked: !isDraft || isManagerView,

    // In-session: only visible and editable when in-session
    inSessionVisible: isInSession || isLocked,
    inSessionEditable: isInSession && !isLocked,

    // Goals: read-only on completed (snapshot)
    goalsReadOnly: isLocked,

    // Private notes always editable (even completed)
    notesEditable: true,

    // Header action determines what button appears
    // 'share'     — draft, report view
    // 'complete'  — in-session, report view
    // 'none'      — all other cases
    headerAction: (() => {
      if (isDraft && !isManagerView) return 'share'
      if (isInSession && !isManagerView) return 'complete'
      return 'none'
    })(),

    isManagerView,
    status,
    isLocked,
  }
}

// ─── SessionCanvasPage ────────────────────────────────────────────────────────

/**
 * SessionCanvasPage — the consolidated one-page session view.
 *
 * All session states (draft → ready → in-session → completed) render here.
 * Sections lock/unlock per `deriveCanvasState`. The page is identical for
 * the report and the manager — viewer derivation happens internally.
 *
 * Props:
 *   session         object  — session object
 *   me              object  — current user
 *   reportName      string  — display name of the session owner
 *   managerNotes    object|null — { reportId, sessionId } — present only in team context
 *   onComplete      function(sessionId, userId)
 *   onCancel        function(sessionId, userId)
 *   onReady         function(sessionId)
 *   onNavigateGoals function
 *   onNavigateGoal  function
 */
export function SessionCanvasPage({
  session,
  me,
  reportName,
  managerNotes = null,
  onComplete,
  onCancel,
  // onReady is part of the public API but submit is handled via usePrep.submitPrep;
  // kept in signature for PR 2 routing compatibility
  onNavigateGoals,
  onNavigateGoal,
}) {
  const canvas = deriveCanvasState(session, me)
  const sessionId = session?.sessionId
  const ownerUserId = session?.userId ?? me?.userId
  const prepId = session?.prepId

  // Prep data is fetched at page level so the SubmitPrepModal in the canvas
  // header has access to both the cards list and submitPrep.
  const {
    prep,
    loading: prepLoading,
    error: prepError,
    reload: prepReload,
    updatePrepCard,
    submitPrep,
  } = usePrep(prepId ?? null, ownerUserId ?? null)

  const [submitOpen, setSubmitOpen] = useState(false)
  const [submitLoading, setSubmitLoading] = useState(false)

  const handleSubmitPrep = useCallback(async () => {
    setSubmitLoading(true)
    try {
      await submitPrep()
      setSubmitOpen(false)
    } finally {
      setSubmitLoading(false)
    }
  }, [submitPrep])

  // Header action node — varies by canvas state
  let headerAction = null
  if (canvas.headerAction === 'share') {
    headerAction = (
      <Button
        variant="primary"
        size="sm"
        onClick={() => setSubmitOpen(true)}
        data-testid="share-with-manager-btn"
      >
        Share with manager
      </Button>
    )
  }

  return (
    <main className="session-canvas" data-testid="session-canvas">
      {/* ── Header ── */}
      <CanvasHeader
        session={session}
        reportName={reportName}
        action={headerAction}
      />

      {/* ── Status banner ── */}
      <StatusBanner status={canvas.status} />

      {/* ── Goals ── */}
      <SectionDivider label="Goals" />
      <GoalsSection
        ownerUserId={ownerUserId}
        sessionId={sessionId}
        goalSnapshot={canvas.goalsReadOnly ? (session?.goalSnapshot ?? null) : null}
        readOnly={canvas.goalsReadOnly}
        onNavigateGoals={onNavigateGoals}
        onNavigateGoal={onNavigateGoal}
      />

      {/* ── Agenda ── */}
      <SectionDivider label="Agenda" />
      <AgendaPlaceholder />

      {/* ── Session preparation ── */}
      <SectionDivider
        label="Session preparation"
        notice={canvas.prepLocked && !canvas.isManagerView ? 'Locked — prep was shared with manager' : undefined}
      />
      <PrepSection
        prep={prep}
        loading={prepLoading}
        error={prepError}
        reload={prepReload}
        updatePrepCard={updatePrepCard}
        isManagerView={canvas.isManagerView}
        disabled={!canvas.prepEditable}
        reportName={reportName}
        sessionId={sessionId}
        me={me}
      />
      <CardTray kind="prep" visible={canvas.prepEditable} />

      {/* ── In session ── */}
      <SectionDivider
        label="In session"
        notice={!canvas.inSessionVisible ? 'Unlocks when session starts' : undefined}
      />
      <InSessionSection
        session={session}
        me={me}
        disabled={!canvas.inSessionEditable}
        inSessionVisible={canvas.inSessionVisible}
        isManagerView={canvas.isManagerView}
        onComplete={onComplete}
        onCancel={onCancel}
      />
      <CardTray kind="inSession" visible={canvas.inSessionEditable} />

      {/* ── Tasks ── */}
      <SectionDivider label="Tasks" />
      <TasksSection session={session} me={me} disabled={canvas.isLocked} />

      {/* ── Private notes ── */}
      <SectionDivider label="Private notes" />
      {managerNotes ? (
        <ManagerNotesPanel
          reportId={managerNotes.reportId}
          sessionId={managerNotes.sessionId}
        />
      ) : (
        <PrivateNotesCard
          sessionId={sessionId}
          initialNotes={session?.privateNotes ?? ''}
          readOnly={false}
        />
      )}

      {/* ── Submit prep modal (Share with manager) ── */}
      <SubmitPrepModal
        open={submitOpen}
        onClose={() => setSubmitOpen(false)}
        onSubmit={handleSubmitPrep}
        cards={prep?.cards ?? []}
        loading={submitLoading}
      />
    </main>
  )
}
